import {
  Viewer,
  Cartographic,
  Cartesian3,
  Rectangle,
  Entity,
  EntityCollection,
  DataSource,
  ImageryLayer,
  Cesium3DTileset,
  TimeDynamicPointCloud,
  HeadingPitchRange,
  HeadingPitchRoll,
  sampleTerrainMostDetailed,
  Matrix3,
  Matrix4,
  PerspectiveFrustum,
  Math as CesiumMath,
  BoundingSphere,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { DeferredValue } from "lib/actions/util";
import { encodeViewImage } from "lib/actions/views/encodeViewImage";

const scratchToPosition = new Cartesian3();
const scratchScaledCamera = new Cartesian3();
const scratchScaledPosition = new Cartesian3();
const scratchScaledDelta = new Cartesian3();
const scratchLowestPoint = new Cartesian3();

type CameraTarget =
  | Entity
  | Entity[]
  | EntityCollection
  | DataSource
  | ImageryLayer
  | Cesium3DTileset
  | TimeDynamicPointCloud;

export default class CameraPlugin implements AttachablePluginInterface {
  private static readonly DEFAULT_FLY_DURATION_SECONDS = 2;
  private static readonly FLY_PITCH_RADIANS = -0.5;
  private static readonly FIT_TO_TARGET_RANGE = 0;
  private static readonly FLY_MIN_HEIGHT_ABOVE_TERRAIN = 200;
  private static readonly MAX_LINE_OF_SIGHT_SAG_METERS = 100;

  private _viewer = new DeferredValue<Viewer>();

  setViewer(viewer: Viewer) {
    this._viewer.set(viewer);
  }

  detachViewer() {
    this._viewer.clear();
  }

  async flyTo(
    target: CameraTarget | Promise<CameraTarget>,
    flyToSeconds: number = CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
  ) {
    if (!target) {
      console.error(`target required.`);
      return;
    }
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    const range =
      "boundingSphere" in target
        ? target.boundingSphere.radius * 2
        : CameraPlugin.FIT_TO_TARGET_RANGE;
    const offset: HeadingPitchRange = {
      heading: 0,
      pitch: CameraPlugin.FLY_PITCH_RADIANS,
      range,
    };
    await viewer.flyTo(target, {
      duration: flyToSeconds,
      offset,
    });
  }

  async flyToBounds(bounds: number[], ignoreTerrainHeight?: boolean) {
    if (bounds.every((val, i) => val === [0, 0, -1, -1][i])) {
      throw new Error("Invalid bounding box");
    }

    const lon = (bounds[0] + bounds[2]) * 0.5;
    const lat = (bounds[1] + bounds[3]) * 0.5;

    const threshold = 3e-4; // Approx 200 metres
    const lonSpanLessThanThreshold = bounds[2] - bounds[0] <= threshold;
    const latSpanLessThanThreshold = bounds[3] - bounds[1] <= threshold;

    if (lonSpanLessThanThreshold && latSpanLessThanThreshold)
      // The bounds are small, so fly to the centre of the bounds instead.
      await this.flyToCartographicWithMinimumHeight(
        Cartographic.fromDegrees(lon, lat),
      );
    else
      await this.flyToRectangleWithMinimumHeight(
        Rectangle.fromDegrees(...bounds),
        ignoreTerrainHeight,
      );
  }

  async flyToCameraTransform(matrix: number[]) {
    const cameraTransform = Matrix4.fromArray(matrix);
    const rotationMatrix = Matrix4.getMatrix3(cameraTransform, new Matrix3());
    const position = Matrix4.getTranslation(cameraTransform, new Cartesian3());
    const direction = Matrix3.getColumn(rotationMatrix, 2, new Cartesian3());
    const directionInverse = Cartesian3.negate(direction, new Cartesian3());
    const up = Matrix3.getColumn(rotationMatrix, 1, new Cartesian3());
    const right = Matrix3.getColumn(rotationMatrix, 0, new Cartesian3());

    try {
      const viewer = await this._viewer.getValueWhenReady();
      if (!viewer) return;

      viewer.camera.flyTo({
        destination: position,
        orientation: {
          direction: directionInverse,
          up,
        },
        duration: 0.7,
      });
    } catch (error) {
      console.error(error);
      return;
    }
  }

  async flyToCartographic(position: Cartographic) {
    await this.flyToCartographicWithMinimumHeight(position);
  }

  async flyToCartographicCoords(lng: number, lat: number, alt: number = 1000) {
    await this.flyToCartographicWithMinimumHeight(
      Cartographic.fromDegrees(lng, lat, alt),
    );
  }

  async flyToCartographicExact(
    position: Cartographic,
    direction: Cartesian3,
    up: Cartesian3,
  ) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    viewer.camera.flyTo({
      destination: Cartographic.toCartesian(position),
      duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
      orientation: { direction, up },
    });
  }

  async flyToCartesian(destination: Cartesian3, orientation: HeadingPitchRoll) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    viewer.camera.flyTo({
      destination,
      orientation,
      duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
    });
  }

  async flyToBoundingSpheres(spheres: BoundingSphere[]) {
    const totalBoundingSphere = BoundingSphere.fromBoundingSpheres(spheres);
    let destination = Cartographic.fromCartesian(
      new Cartesian3(
        totalBoundingSphere.center.x,
        totalBoundingSphere.center.y,
        totalBoundingSphere.center.z,
      ),
    );

    destination.height = totalBoundingSphere.radius;
    await this.flyToCartographicWithMinimumHeight(destination);
  }

  async flyToEntities(entities: Entity[]) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    const offset = {
      heading: 0,
      pitch: -Math.PI / 2,
      range: CameraPlugin.FIT_TO_TARGET_RANGE,
    };
    await viewer.flyTo(entities, {
      duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
      offset,
    });
  }

  async captureCameraScreenshot() {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    viewer.scene.render();
    return encodeViewImage(viewer.scene.canvas);
  }

  async captureCameraPosition() {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    const m = viewer.camera.inverseViewMatrix;
    return Matrix4.toArray(m);
  }

  isPositionVisible(position: Cartesian3 | null | undefined): boolean {
    const viewer = this._viewer.current;
    if (!position || !viewer) return false;

    const camera = viewer.camera;
    const ellipsoid = viewer.scene.globe.ellipsoid;
    const cameraPosition = camera.positionWC;
    const toPosition = Cartesian3.subtract(
      position,
      cameraPosition,
      scratchToPosition,
    );
    if (Cartesian3.dot(toPosition, camera.directionWC) <= 0) return false;

    // Find the point on the line of sight nearest the earth's centre. Scaled
    // space turns the ellipsoid into a unit sphere, and since the transform is
    // linear the segment parameter carries back to world space unchanged.
    const scaledCamera = ellipsoid.transformPositionToScaledSpace(
      cameraPosition,
      scratchScaledCamera,
    );
    const scaledDelta = Cartesian3.subtract(
      ellipsoid.transformPositionToScaledSpace(position, scratchScaledPosition),
      scaledCamera,
      scratchScaledDelta,
    );
    const scaledLengthSquared = Cartesian3.magnitudeSquared(scaledDelta);
    if (scaledLengthSquared === 0) return true;

    const t = -Cartesian3.dot(scaledCamera, scaledDelta) / scaledLengthSquared;
    // The lowest point is an endpoint, so the line never dips below either end.
    if (t <= 0 || t >= 1) return true;

    const lowestPoint = Cartesian3.lerp(
      cameraPosition,
      position,
      t,
      scratchLowestPoint,
    );
    const lowestHeight = ellipsoid.cartesianToCartographic(lowestPoint)?.height;
    const cameraHeight =
      ellipsoid.cartesianToCartographic(cameraPosition)?.height;
    const positionHeight = ellipsoid.cartesianToCartographic(position)?.height;
    // cartesianToCartographic is undefined near the earth's centre, which the
    // line of sight only reaches when it passes straight through the globe.
    if (
      lowestHeight === undefined ||
      cameraHeight === undefined ||
      positionHeight === undefined
    ) {
      return false;
    }

    // A straight line of sight only sags below its endpoints because of the
    // earth's curvature, by roughly length² / (8 × earth radius): ~2 cm over
    // 1 km and ~50 m over 50 km. Sagging further means the line passes through
    // the globe.
    return (
      lowestHeight >=
      Math.min(cameraHeight, positionHeight) -
        CameraPlugin.MAX_LINE_OF_SIGHT_SAG_METERS
    );
  }

  private async flyToCartographicWithMinimumHeight(destination: Cartographic) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;
    await new Promise<void>((res, rej) => {
      //use promise executor as Cesium 'Promise' is not a real promise and has quirks
      sampleTerrainMostDetailed(viewer.terrainProvider, [
        destination.clone(),
      ]).then(
        ([destinationAdjusted]) => {
          destinationAdjusted.height = Math.max(
            destinationAdjusted.height +
              CameraPlugin.FLY_MIN_HEIGHT_ABOVE_TERRAIN,
            destination.height,
          );
          viewer.camera.flyTo({
            destination: Cartographic.toCartesian(destinationAdjusted),
            duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
          });
          res();
        },
        () => {
          viewer.camera.flyTo({
            destination: Cartographic.toCartesian(destination),
            duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
            complete: () => viewer.scene.camera.moveBackward(1000),
          });
          res();
        },
      );
    });
  }

  private async flyToRectangleWithMinimumHeight(
    rectangle: Rectangle,
    ignoreTerrainHeight?: boolean,
  ) {
    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    if (!viewer.terrainProvider?.availability || ignoreTerrainHeight) {
      viewer.camera.flyTo({
        destination: rectangle,
        duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
      });

      return;
    }

    const maxTerrainHeight = await getMaximumHeightForRectangleArea();

    const center = Rectangle.center(rectangle);

    const longitudeStretch = Cartesian3.distance(
      Cartesian3.fromRadians(rectangle.west, center.latitude),
      Cartesian3.fromRadians(rectangle.east, center.latitude),
    );
    const latitudeStretch = Cartesian3.distance(
      Cartesian3.fromRadians(center.longitude, rectangle.south),
      Cartesian3.fromRadians(center.longitude, rectangle.north),
    );

    const maxDimension = Math.max(longitudeStretch, latitudeStretch);

    const minFov = Math.min(
      (<PerspectiveFrustum>viewer.camera.frustum).fovy,
      (<PerspectiveFrustum>viewer.camera.frustum).fov,
    );
    const cameraHeightAboveTerrain =
      maxDimension / 2.0 / Math.tan(minFov / 2.0);

    viewer.camera.flyTo({
      destination: Cartesian3.fromRadians(
        center.longitude,
        center.latitude,
        cameraHeightAboveTerrain + maxTerrainHeight,
      ),
      duration: CameraPlugin.DEFAULT_FLY_DURATION_SECONDS,
    });

    async function getMaximumHeightForRectangleArea() {
      const gridSize = 10;
      const samples: Cartographic[] = [];

      for (let i = 0; i <= gridSize; i++) {
        for (let j = 0; j <= gridSize; j++) {
          const lon = CesiumMath.lerp(
            rectangle.west,
            rectangle.east,
            i / gridSize,
          );
          const lat = CesiumMath.lerp(
            rectangle.south,
            rectangle.north,
            j / gridSize,
          );
          samples.push(new Cartographic(lon, lat));
        }
      }

      await sampleTerrainMostDetailed(viewer.terrainProvider, samples);

      // A sample over a missing terrain tile has no height; skip it rather than
      // letting it turn the maximum (and the camera destination) into NaN.
      const heights = samples
        .map((s) => s.height)
        .filter((height) => Number.isFinite(height));
      return heights.length > 0 ? Math.max(...heights) : 0;
    }
  }
}
